import { GRID_LAYOUTS, selectGridLayout } from '../helper/custom-layout';
import * as React from 'react';
import { useSize } from './internal/useResizeObserver';

/**
 * The `useCustomLayout` hook tries to select the best layout to fit all tiles.
 * If the available screen space is not enough, it will reduce the number of maximum visible
 * tiles and select a layout that still works visually within the given limitations.
 * As the order of tiles changes over time, the hook tries to keep visual updates to a minimum
 * while trying to display important tiles such as speaking participants or screen shares.
 *
 * @example
 * ```tsx
 * const { layout } = useCustomLayout(gridElement, trackCount);
 * ```
 * @public
 */
export function useCustomLayout(
  /** HTML element that contains the grid. */
  gridElement: React.RefObject<HTMLDivElement>,
  /** Count of tracks that should get layed out */
  trackCount: number,
): { focusHostRow: boolean, radius: string, profile_img_dimensions: string } {
  const gridLayouts = GRID_LAYOUTS;
  useSize(gridElement);
  const layout = selectGridLayout(gridLayouts, trackCount);

  React.useEffect(() => {
    if (gridElement.current && layout) {
      gridElement.current.style.setProperty('--cl-col-count', layout?.columns.toString());
      gridElement.current.style.setProperty('--cl-row-count', layout?.rows.toString());
    }
  }, [gridElement, layout]);

  return { focusHostRow: layout.focusHostRow, radius: layout.radius ?? '24px', profile_img_dimensions: trackCount > 2 ? "150px" : "200px" };
}
